import React, { useState, useContext, useEffect } from 'react';
import { Box, Typography, Stack, Button, TextField, MenuItem, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, CircularProgress, ToggleButton, ToggleButtonGroup, Divider } from '@mui/material';
import { API } from '../../conf';
import { Context } from '../../App';
import moment from 'moment';

const MOIS = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre']

export default function RecapDonateur({ donateur, eglise, eglises = [], close }) {
    const { updateConnect, exercices } = useContext(Context)
    const [mode, setMode] = useState('annee')
    const [selectedYear, setSelectedYear] = useState(exercices[0] || new Date().getFullYear())
    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth())
    const [start_date, setStart] = useState(moment().subtract(1, 'month').format('YYYY-MM-DD'))
    const [end_date, setEnd] = useState(moment().format('YYYY-MM-DD'))
    const [dons, setDons] = useState(null)
    const [donEglises, setDonEglises] = useState([])
    const [registeredEglises, setRegisteredEglises] = useState([])
    const [loading, setLoad] = useState(false)

    function fetchDons(start, end) {
        setLoad(true)
        const query = { donateur: donateur.num, $and: [{ date: { $gte: new Date(start) } }, { date: { $lte: new Date(new Date(end).setHours(23, 59, 59)) } }] }
        fetch(API.concat(`/dons/by/${JSON.stringify(query)}`), {
            method: 'GET',
            headers: { Accept: 'application/json', 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + sessionStorage.getItem('token') }
        }).then(async res => {
            const token = res.headers.get('x-auth-token')
            if (res.status === 401) { sessionStorage.clear(); updateConnect(false) }
            else {
                if (token && token !== sessionStorage.getItem('token') && token !== '') sessionStorage.setItem('token', token)
                if (res.status === 200) {
                    const data = await res.json()
                    setDons(data)
                    const egliseNums = [...new Set(data.map(d => d.eglise))]
                    if (egliseNums.length > 0) {
                        fetch(API.concat(`/eglises/by/${JSON.stringify({ number: { $in: egliseNums } })}`), {
                            method: 'GET',
                            headers: { Accept: 'application/json', 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + sessionStorage.getItem('token') }
                        }).then(async r => {
                            const t = r.headers.get('x-auth-token')
                            if (t && t !== sessionStorage.getItem('token') && t !== '') sessionStorage.setItem('token', t)
                            if (r.status === 200) setDonEglises(await r.json())
                        })
                    }
                }
            }
            setLoad(false)
        })
    }

    useEffect(() => {
        if (!eglise) {
            fetch(API.concat(`/eglises/by/${JSON.stringify({ donateurs: donateur.num })}`), {
                method: 'GET',
                headers: { Accept: 'application/json', 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + sessionStorage.getItem('token') }
            }).then(async res => {
                if (res.status === 200) setRegisteredEglises(await res.json())
            })
        }
    }, [])

    function handleApply() {
        if (mode === 'mois') {
            const start = `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}-01`
            const end = moment(start).endOf('month').format('YYYY-MM-DD')
            fetchDons(start, end)
        } else if (mode === 'annee') {
            fetchDons(`${selectedYear}-01-01`, `${selectedYear}-12-31`)
        } else {
            fetchDons(start_date, end_date)
        }
    }

    function get_eglise_label(num) {
        const eg = donEglises.find(e => e.number === num)
        return eg ? eg.nom : `Église ${num}`
    }

    function groupByMonthAndEglise(dons) {
        const map = {}
        dons.forEach(don => {
            const key = `${moment.utc(don.date).format('YYYY-MM')}_${don.eglise}`
            if (!map[key]) map[key] = { key, monthKey: moment.utc(don.date).format('YYYY-MM'), egliseNum: don.eglise, dime: 0, offrande_globale: 0, offrande_spe_eglise: 0, offrande_spe_feam: 0 }
            map[key].dime += don.dime
            map[key].offrande_globale += don.offrande_globale
            map[key].offrande_spe_eglise += don.offrande_spe_eglise.reduce((t, o) => t + o.montant, 0)
            map[key].offrande_spe_feam += don.offrande_spe_feam.reduce((t, o) => t + o.montant, 0)
        })
        return Object.values(map).sort((a, b) => a.key.localeCompare(b.key)).map(row => ({
            ...row,
            label: `${MOIS[parseInt(row.monthKey.split('-')[1]) - 1]} ${row.monthKey.split('-')[0]}`,
            total: row.dime + row.offrande_globale + row.offrande_spe_eglise + row.offrande_spe_feam
        }))
    }

    const rows = dons ? groupByMonthAndEglise(dons) : []
    const totaux = rows.reduce((t, r) => ({
        dime: t.dime + r.dime,
        offrande_globale: t.offrande_globale + r.offrande_globale,
        offrande_spe_eglise: t.offrande_spe_eglise + r.offrande_spe_eglise,
        offrande_spe_feam: t.offrande_spe_feam + r.offrande_spe_feam,
        total: t.total + r.total
    }), { dime: 0, offrande_globale: 0, offrande_spe_eglise: 0, offrande_spe_feam: 0, total: 0 })

    const headerSx = { fontWeight: 'bold', backgroundColor: 'primary.light', color: 'white' }
    const totalSx = { fontWeight: 'bold', backgroundColor: 'rgba(37,87,96,0.08)' }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, px: 3, py: 2 }}>
            <Typography variant='h5' color='primary' textAlign='center' fontWeight={600} mt={1}>
                Récapitulatif — {donateur.genre} {donateur.nom.toUpperCase()} {donateur.prenom1} {donateur.prenom2 || ''}
            </Typography>

            {/* Infos donateur */}
            <Paper variant='outlined' sx={{ p: 2 }}>
                <Stack direction='row' flexWrap='wrap' gap={3}>
                    {donateur.mail && <Box>
                        <Typography variant='caption' color='text.secondary'>Email</Typography>
                        <Typography>{donateur.mail}</Typography>
                    </Box>}
                    {donateur.tel && <Box>
                        <Typography variant='caption' color='text.secondary'>Téléphone</Typography>
                        <Typography>{donateur.tel}</Typography>
                    </Box>}
                    {(donateur.voie || donateur.ville) && <Box>
                        <Typography variant='caption' color='text.secondary'>Adresse</Typography>
                        <Typography>{[donateur.voie, donateur.adresse_complement, [donateur.zip_code, donateur.ville?.toUpperCase()].filter(Boolean).join(' ')].filter(Boolean).join(', ')}</Typography>
                    </Box>}
                    {(eglise || registeredEglises.length > 0) && <Box>
                        <Typography variant='caption' color='text.secondary'>Église{registeredEglises.length > 1 ? 's' : ''}</Typography>
                        <Typography>
                            {eglise ? eglise.nom : registeredEglises.map(e => e.nom).join(', ')}
                        </Typography>
                    </Box>}
                </Stack>
            </Paper>

            <Divider />

            {/* Sélecteur de période */}
            <Stack direction='row' spacing={2} alignItems='center' flexWrap='wrap'>
                <ToggleButtonGroup size='small' value={mode} exclusive onChange={(e, v) => { if (v) { setMode(v); setDons(null) } }}>
                    <ToggleButton value='mois'>Mois</ToggleButton>
                    <ToggleButton value='annee'>Année</ToggleButton>
                    <ToggleButton value='libre'>Libre</ToggleButton>
                </ToggleButtonGroup>

                {mode === 'mois' && <>
                    <TextField select size='small' label='Année' value={selectedYear} onChange={e => setSelectedYear(e.target.value)} sx={{ minWidth: 100 }}>
                        {exercices.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                    </TextField>
                    <TextField select size='small' label='Mois' value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)} sx={{ minWidth: 130 }}>
                        {MOIS.map((m, i) => <MenuItem key={i} value={i}>{m}</MenuItem>)}
                    </TextField>
                </>}

                {mode === 'annee' && <TextField select size='small' label='Année' value={selectedYear} onChange={e => setSelectedYear(e.target.value)} sx={{ minWidth: 100 }}>
                    {exercices.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                </TextField>}

                {mode === 'libre' && <>
                    <TextField size='small' type='date' label='Début' InputLabelProps={{ shrink: true }} value={start_date}
                        onChange={e => { setStart(e.target.value); if (end_date && new Date(end_date) <= new Date(e.target.value)) setEnd('') }} />
                    <TextField size='small' type='date' label='Fin' InputLabelProps={{ shrink: true }} value={end_date}
                        inputProps={{ min: start_date }}
                        onChange={e => setEnd(e.target.value)} />
                </>}

                <Button variant='contained' onClick={handleApply} disabled={loading || (mode === 'libre' && (!start_date || !end_date))}>
                    {loading ? <CircularProgress size={20} color='inherit' /> : 'Appliquer'}
                </Button>
            </Stack>

            {/* Tableau */}
            {dons !== null && (rows.length === 0
                ? <Typography color='text.secondary' textAlign='center' mt={2}>Aucun don sur cette période.</Typography>
                : <TableContainer component={Paper} variant='outlined'>
                    <Table size='small'>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={headerSx}>Mois</TableCell>
                                <TableCell sx={headerSx}>Église</TableCell>
                                <TableCell sx={headerSx} align='center'>Dîme</TableCell>
                                <TableCell sx={headerSx} align='center'>Off. globale</TableCell>
                                <TableCell sx={headerSx} align='center'>Off. spé locale</TableCell>
                                <TableCell sx={headerSx} align='center'>Off. spé fédérale</TableCell>
                                <TableCell sx={headerSx} align='center'>Total</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map(row => <TableRow key={row.key}>
                                <TableCell>{row.label}</TableCell>
                                <TableCell>{get_eglise_label(row.egliseNum)}</TableCell>
                                <TableCell align='center'>{row.dime.toFixed(2)}€</TableCell>
                                <TableCell align='center'>{row.offrande_globale.toFixed(2)}€</TableCell>
                                <TableCell align='center'>{row.offrande_spe_eglise.toFixed(2)}€</TableCell>
                                <TableCell align='center'>{row.offrande_spe_feam.toFixed(2)}€</TableCell>
                                <TableCell align='center' sx={{ fontWeight: 'bold' }}>{row.total.toFixed(2)}€</TableCell>
                            </TableRow>)}
                            <TableRow>
                                <TableCell sx={totalSx} colSpan={2}>Total</TableCell>
                                <TableCell sx={totalSx} align='center'>{totaux.dime.toFixed(2)}€</TableCell>
                                <TableCell sx={totalSx} align='center'>{totaux.offrande_globale.toFixed(2)}€</TableCell>
                                <TableCell sx={totalSx} align='center'>{totaux.offrande_spe_eglise.toFixed(2)}€</TableCell>
                                <TableCell sx={totalSx} align='center'>{totaux.offrande_spe_feam.toFixed(2)}€</TableCell>
                                <TableCell sx={totalSx} align='center'>{totaux.total.toFixed(2)}€</TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <Button variant='text' sx={{ alignSelf: 'flex-end' }} onClick={close}>Fermer</Button>
        </Box>
    )
}
